import { useEffect, useState } from "react";
import { Link, Redirect, useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCustomerProfile,
  useUpdateCustomerProfile,
} from "@orbit/api-client-react";
import type { CustomerGender, LanguageCode } from "@orbit/api-zod";
import { useAuth } from "../hooks/useAuth";
import { Wordmark } from "../components/Brand";

/**
 * /profile/edit — name + birthday + language. Phone & email live on
 * their own dedicated change flows (separate sub-pages) because they
 * each gate behind an OTP. We deliberately don't expose phone/email
 * fields here so the user can't bypass the gating accidentally.
 */
export function ProfileEditPage() {
  const { isAuthenticated } = useAuth();
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const profileQ = useCustomerProfile({ enabled: isAuthenticated });
  const updateMut = useUpdateCustomerProfile();

  const [name, setName] = useState("");
  const [birthday, setBirthday] = useState("");
  const [language, setLanguage] = useState<LanguageCode>("en-PH");
  // Migration 031 — optional profile fields. Empty string in the
  // form means "clear" (the API accepts null; the zod transform
  // coerces "" → null on the server side).
  const [gender, setGender] = useState<CustomerGender | "">("");
  const [addressLine, setAddressLine] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [occupation, setOccupation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [seeded, setSeeded] = useState(false);

  // Seed the form from the fetched profile exactly once. After that we
  // own the buffer so re-fetches don't clobber edits in progress.
  useEffect(() => {
    if (seeded || !profileQ.data) return;
    setName(profileQ.data.name);
    setBirthday(profileQ.data.birthday ?? "");
    setLanguage(profileQ.data.languagePreference);
    setGender(profileQ.data.gender ?? "");
    setAddressLine(profileQ.data.addressLine ?? "");
    setCity(profileQ.data.city ?? "");
    setProvince(profileQ.data.province ?? "");
    setPostalCode(profileQ.data.postalCode ?? "");
    setOccupation(profileQ.data.occupation ?? "");
    setSeeded(true);
  }, [profileQ.data, seeded]);

  if (!isAuthenticated) return <Redirect to="/" />;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setOkMsg(null);
    if (name.trim().length < 1) {
      setError("Please enter your name.");
      return;
    }
    try {
      await updateMut.mutateAsync({
        name: name.trim(),
        birthday: birthday.trim() ? birthday.trim() : null,
        languagePreference: language,
        // Migration 031 — send all optional fields on every save.
        // Empty strings get coerced to null by the server-side zod
        // transform, so clearing a field works the same as setting
        // it. gender uses an explicit null when the placeholder
        // option is selected.
        gender: gender === "" ? null : gender,
        addressLine: addressLine,
        city: city,
        province: province,
        postalCode: postalCode,
        occupation: occupation,
      });
      await qc.invalidateQueries({ queryKey: ["customer-profile"] });
      await qc.invalidateQueries({ queryKey: ["customer-me"] });
      setOkMsg("Saved.");
      setTimeout(() => navigate("/profile"), 700);
    } catch (err) {
      const e = err as { apiError?: { message?: string } };
      setError(e.apiError?.message ?? "Couldn't save your profile.");
    }
  }

  return (
    <div className="min-h-screen pb-16">
      <div className="mx-auto max-w-md px-6 pt-8">
        <header className="flex items-center justify-between">
          <Wordmark size="sm" />
          <Link href="/profile" className="text-xs text-ink-dark underline">
            Back
          </Link>
        </header>
        <h1 className="mt-6 text-xl font-bold text-navy">Edit profile</h1>

        {profileQ.isLoading ? (
          <p className="mt-6 text-sm text-ink-dark">Loading…</p>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-ink-dark/70">
                Name
              </span>
              <input
                className="input mt-1"
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, 120))}
                required
              />
            </label>

            <label className="block">
              <span className="text-xs uppercase tracking-wider text-ink-dark/70">
                Birthday
              </span>
              <input
                type="date"
                className="input mt-1"
                value={birthday}
                onChange={(e) => setBirthday(e.target.value)}
              />
              <span className="text-[11px] text-ink-dark/70">
                Optional. Lets merchants send birthday surprises.
              </span>
            </label>

            <label className="block">
              <span className="text-xs uppercase tracking-wider text-ink-dark/70">
                Language
              </span>
              <select
                className="input mt-1"
                value={language}
                onChange={(e) => setLanguage(e.target.value as LanguageCode)}
              >
                <option value="en-PH">English (Philippines)</option>
                <option value="fil-PH">Filipino</option>
                <option value="tl-PH">Tagalog</option>
              </select>
            </label>

            {/* Migration 031 — optional details. Grouped under a
                section header so the form makes it visually obvious
                the rest is opt-in. */}
            <div className="pt-2">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
                More about you (optional)
              </h2>
              <p className="mt-1 text-[11px] text-ink-dark/70">
                All fields below are optional. Filling them helps
                merchants tailor offers and deliveries to you. You can
                update or clear them anytime.
              </p>
            </div>

            <label className="block">
              <span className="text-xs uppercase tracking-wider text-ink-dark/70">
                Gender
              </span>
              <select
                className="input mt-1"
                value={gender}
                onChange={(e) =>
                  setGender(e.target.value as CustomerGender | "")
                }
              >
                <option value="">Prefer not to say</option>
                <option value="FEMALE">Female</option>
                <option value="MALE">Male</option>
                <option value="OTHER">Other</option>
                <option value="PREFER_NOT_TO_SAY">Prefer not to say</option>
              </select>
            </label>

            <label className="block">
              <span className="text-xs uppercase tracking-wider text-ink-dark/70">
                Address
              </span>
              <input
                className="input mt-1"
                placeholder="123 Mabini St, Brgy San Antonio"
                value={addressLine}
                onChange={(e) => setAddressLine(e.target.value.slice(0, 200))}
              />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-ink-dark/70">
                  City
                </span>
                <input
                  className="input mt-1"
                  placeholder="Quezon City"
                  value={city}
                  onChange={(e) => setCity(e.target.value.slice(0, 80))}
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-ink-dark/70">
                  Province
                </span>
                <input
                  className="input mt-1"
                  placeholder="Metro Manila"
                  value={province}
                  onChange={(e) => setProvince(e.target.value.slice(0, 80))}
                />
              </label>
            </div>

            <label className="block">
              <span className="text-xs uppercase tracking-wider text-ink-dark/70">
                Postal code
              </span>
              <input
                className="input mt-1"
                inputMode="numeric"
                placeholder="1100"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value.slice(0, 16))}
              />
            </label>

            <label className="block">
              <span className="text-xs uppercase tracking-wider text-ink-dark/70">
                Occupation
              </span>
              <input
                className="input mt-1"
                placeholder="Teacher, Engineer, Student…"
                value={occupation}
                onChange={(e) => setOccupation(e.target.value.slice(0, 80))}
              />
            </label>

            {error && <p className="text-red-600 text-sm">{error}</p>}
            {okMsg && <p className="text-green-700 text-sm">{okMsg}</p>}
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={updateMut.isPending}
            >
              {updateMut.isPending ? "Saving…" : "Save changes"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
